/**
 * Section-visibility menu — Radix Popover anchored to the gear icon in
 * the Entity Details panel header.
 *
 * Stream A PR 5.4 (platform-only) → Profiles increment 7 (every entity
 * type). Renders a checkbox list of the selected entity type's section
 * ids; toggling a checkbox optimistically updates the persisted
 * ``working_state.entity_details.<entityType>.sections`` array via the
 * existing preferences API (the **working state**, never a saved
 * Profile — REQ-UI-092).
 *
 * Per Flow Q §"Frame 4" the menu closes on first toggle — matches the
 * storyboard's documented behavior. (The "sticky-checkbox-menus"
 * alternative noted in Flow Q's TBD is not the MVP behavior.)
 *
 * Cross-references:
 *   - REQ-UI-092 (per-type section visibility; gear mutates working state).
 *   - REQ-UI-050 (section-visibility menu + persistence).
 *   - Flow Q §"Frame 4".
 *   - ADR-024 §4 / §6; ADR-033 §10.
 */
import * as Popover from "@radix-ui/react-popover";
import { Gear } from "@phosphor-icons/react";
import { useState } from "react";
import {
  useSectionVisibility,
  SECTION_TITLES,
  type EntityDetailType,
} from "@/entity-details/use-section-visibility";

export function SectionVisibilityMenu({
  entityType,
}: {
  /** Which entity type's sections the menu lists + toggles. */
  entityType: EntityDetailType;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isVisible, setVisible, sectionIds } = useSectionVisibility(entityType);
  const titles: Record<string, string> = SECTION_TITLES[entityType];

  const handleToggle = async (sectionId: string, currentlyVisible: boolean): Promise<void> => {
    setError(null);
    const result = await setVisible(sectionId, !currentlyVisible);
    if (result.status === "error") {
      setError(result.message);
      // Keep the menu open so the operator can see the error.
      return;
    }
    // Per Flow Q §"Frame 4" — close on first toggle (the documented MVP
    // behavior; sticky-menu alternative is TBD).
    setOpen(false);
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setError(null);
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="Section visibility"
          title="Section visibility"
          className="rounded p-1 text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
          data-testid="section-visibility-gear"
        >
          <Gear size={16} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={6}
          className="z-50 min-w-[200px] overflow-hidden rounded-md border border-border bg-bg-overlay shadow-lg"
          data-testid="section-visibility-menu"
          aria-label="Entity details section visibility"
        >
          <div className="px-3 py-2">
            <h4 className="text-caption font-medium uppercase tracking-wider text-text-secondary">
              Sections
            </h4>
          </div>
          <ul role="menu" className="border-t border-border py-1">
            {sectionIds.map((id) => {
              const visible = isVisible(id);
              const title = titles[id] ?? id;
              return (
                <li key={id} role="none">
                  <label
                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-body-sm text-text-primary hover:bg-bg-elevated"
                    data-testid={`section-visibility-row-${id}`}
                  >
                    <input
                      type="checkbox"
                      checked={visible}
                      onChange={() => void handleToggle(id, visible)}
                      data-testid={`section-visibility-checkbox-${id}`}
                      className="h-3.5 w-3.5 cursor-pointer"
                    />
                    <span>{title}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          {error !== null && (
            <div
              className="border-t border-border bg-bg-elevated px-3 py-2 text-caption text-destructive"
              role="alert"
              data-testid="section-visibility-error"
            >
              {error}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
